import React, { useEffect, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import './Layout.scss';
import { Outlet, useLocation } from 'react-router-dom';
import ErrorBoundary from '../component/ErrorBoundary';
import UsageLimitBanner from '../component/DashboardBanner/UsageLimitBanner';

// Global URLSearchParams fallback patch so searchParams.get(), has(), toString(), and forEach() read from sessionStorage when URL bar is clean
if (!URLSearchParams.prototype._isPatched) {
	const originalGet = URLSearchParams.prototype.get;
	const originalHas = URLSearchParams.prototype.has;
	const originalToString = URLSearchParams.prototype.toString;
	const originalForEach = URLSearchParams.prototype.forEach;

	const getSavedParams = () => {
		try {
			const saved = sessionStorage.getItem(`url_params_${window.location.pathname}`);
			return saved ? new URLSearchParams(saved) : null;
		} catch (e) {
			return null;
		}
	};

	URLSearchParams.prototype.get = function (name) {
		const val = originalGet.call(this, name);
		if (val !== null && val !== undefined && val !== '') return val;
		const savedParams = getSavedParams();
		if (savedParams) {
			const savedVal = originalGet.call(savedParams, name);
			if (savedVal !== null && savedVal !== undefined && savedVal !== '') return savedVal;
		}
		return val;
	};

	URLSearchParams.prototype.has = function (name) {
		if (originalHas.call(this, name)) return true;
		const savedParams = getSavedParams();
		return savedParams ? originalHas.call(savedParams, name) : false;
	};

	URLSearchParams.prototype.toString = function () {
		const str = originalToString.call(this);
		if (str) return str;
		const savedParams = getSavedParams();
		return savedParams ? originalToString.call(savedParams) : '';
	};

	URLSearchParams.prototype.forEach = function (callback, thisArg) {
		const str = originalToString.call(this);
		if (str) {
			return originalForEach.call(this, callback, thisArg);
		}
		const savedParams = getSavedParams();
		if (savedParams) {
			return originalForEach.call(savedParams, callback, thisArg);
		}
	};

	URLSearchParams.prototype._isPatched = true;
}

const Layout = () => {
	const [collapsed, setCollapsed] = useState(false);
	const inputRef = useRef(null);

	const location = useLocation();

	// Store search parameters in sessionStorage per route and keep URL address bar 100% clean
	useEffect(() => {
		const isDetailPageOf = (newPath, oldPath) => {
			if (!newPath || !oldPath) return false;
			if (newPath === oldPath) return true;
			const cleanOld = oldPath.replace(/\/$/, '');
			const cleanNew = newPath.replace(/\/$/, '');
			return (
				cleanNew.startsWith(`${cleanOld}-`) ||
				cleanNew.startsWith(`${cleanOld}/`)
			);
		};

		const prevPath = sessionStorage.getItem('last_visited_pathname');
		const currentPath = location.pathname;

		if (prevPath && prevPath !== currentPath) {
			// If navigating to another module/submodule (not a detail view of prevPath), clear stored params for prevPath
			if (!isDetailPageOf(currentPath, prevPath) && !isDetailPageOf(prevPath, currentPath)) {
				sessionStorage.removeItem(`url_params_${prevPath}`);
			}
		}

		sessionStorage.setItem('last_visited_pathname', currentPath);

		const storageKey = `url_params_${location.pathname}`;

		if (location.search) {
			sessionStorage.setItem(storageKey, location.search);
			window.history.replaceState(null, '', location.pathname);
		}
	}, [location.search, location.pathname]);

	return (
		<div>
			<div>
				<Navbar
					collapsed={collapsed}
					inputRef={inputRef}
					setCollapsed={setCollapsed}
				/>
				<div className="d-flex">
					<Sidebar
						inputRef={inputRef}
						collapsed={collapsed}
						setCollapsed={setCollapsed}
					/>
					<div className="layout-wrapper">
						{/*
						 * Rendered in the layout (not inside a specific page) so a 90%+
						 * usage alert stays visible across the whole dashboard rather than
						 * being a per-page notice that disappears on navigation.
						 *
						 * Placed INSIDE .layout-wrapper rather than above the d-flex row:
						 * out there it was a sibling of the sidebar, so it spanned the
						 * full viewport width, sat over the sidebar and pushed the entire
						 * app down. .layout-wrapper already starts below the navbar
						 * (margin-top: 60px) and is width-constrained beside the sidebar
						 * at every breakpoint, so the banner now occupies the content
						 * column only and cannot overflow it.
						 */}
						<UsageLimitBanner />
						{/*
						 * Wraps the routed page only, so a crash inside one page keeps the
						 * navbar and sidebar usable and the user can navigate away —
						 * rather than React unmounting the whole tree and leaving a blank
						 * screen. `key` on the route path resets the boundary on
						 * navigation, otherwise a page that errored once would stay
						 * errored after moving elsewhere.
						 */}
						<ErrorBoundary key={location.pathname}>
							<Outlet />
						</ErrorBoundary>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Layout;
